import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";
import { useMobile } from "@/hooks/useMobile";
import { toast } from "sonner";
import { format, startOfMonth, isAfter } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ClipboardList,
  Clock,
  AlertTriangle,
  CheckCircle,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminTaskCreationModal } from "@/components/admin/AdminTaskCreationModal";

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "member", label: "Member-Related" },
  { value: "asset", label: "Asset-Related" },
  { value: "contribution", label: "Contribution-Related" },
  { value: "resolution", label: "Resolution-Related" },
  { value: "documentation", label: "Documentation-Related" },
  { value: "banking", label: "Banking & Finance" },
  { value: "business", label: "Business-Related" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "blocked", label: "Blocked" },
];

const SOURCE_OPTIONS = [
  { value: "all", label: "All Tasks" },
  { value: "admin", label: "Admin-Assigned Only" },
  { value: "user", label: "User-Created Only" },
];

function getStatusBadge(status: string) {
  const config: Record<string, { label: string; className: string }> = {
    not_started: { label: "Not Started", className: "bg-muted text-muted-foreground" },
    in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700" },
    completed: { label: "Completed", className: "bg-green-100 text-green-700" },
    blocked: { label: "Blocked", className: "bg-red-100 text-red-700" },
  };
  const { label, className } = config[status] || config.not_started;
  return <Badge className={className}>{label}</Badge>;
}

function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    member: "Member-Related",
    asset: "Asset-Related",
    contribution: "Contribution-Related",
    resolution: "Resolution-Related",
    documentation: "Documentation-Related",
    banking: "Banking & Finance",
    business: "Business-Related",
    other: "Other",
  };
  return labels[category] || "Other";
}

export default function AdminTaskManagement() {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isSupportAdmin } = useCurrentAdmin();
  const canCreateTasks = isSuperAdmin() || isSupportAdmin();
  const isMobile = useMobile();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("admin");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Fetch tasks with filters - fetch tasks first, then enrich with profile/UNA data
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ["admin-tasks", statusFilter, categoryFilter, sourceFilter, searchTerm, page],
    queryFn: async () => {
      // First, fetch the tasks
      let query = supabase
        .from("custom_tasks")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }
      if (categoryFilter !== "all") {
        query = query.eq("category_type", categoryFilter);
      }
      if (sourceFilter === "admin") {
        query = query.eq("is_admin_assigned", true);
      } else if (sourceFilter === "user") {
        query = query.eq("is_admin_assigned", false);
      }
      if (searchTerm) {
        query = query.ilike("task_title", `%${searchTerm}%`);
      }

      const { data: tasks, error, count } = await query;
      if (error) throw error;
      
      if (!tasks || tasks.length === 0) {
        return { tasks: [], total: count || 0 };
      }

      // Get unique user IDs and UNA IDs
      const userIds = [...new Set(tasks.map(t => t.user_id).filter(Boolean))];
      const unaIds = [...new Set(tasks.map(t => t.una_id).filter(Boolean))];

      // Fetch profiles for all users
      const { data: profiles } = userIds.length > 0 
        ? await supabase.from("profiles").select("id, full_name, email, avatar_url").in("id", userIds)
        : { data: [] };

      // Fetch UNAs
      const { data: unas } = unaIds.length > 0
        ? await supabase.from("unas").select("id, name").in("id", unaIds)
        : { data: [] };

      // Create lookup maps
      const profileMap = new Map((profiles || []).map(p => [p.id, p]));
      const unaMap = new Map((unas || []).map(u => [u.id, u]));

      // Enrich tasks with profile and UNA data
      const enrichedTasks = tasks.map(task => ({
        ...task,
        profiles: task.user_id ? profileMap.get(task.user_id) || null : null,
        unas: task.una_id ? unaMap.get(task.una_id) || null : null,
      }));

      return { tasks: enrichedTasks, total: count || 0 };
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ["admin-task-stats"],
    queryFn: async () => {
      const today = new Date();
      const monthStart = startOfMonth(today);

      // Total admin tasks
      const { count: totalAdmin } = await supabase
        .from("custom_tasks")
        .select("*", { count: "exact", head: true })
        .eq("is_admin_assigned", true);

      // Pending (not started)
      const { count: pending } = await supabase
        .from("custom_tasks")
        .select("*", { count: "exact", head: true })
        .eq("is_admin_assigned", true)
        .eq("status", "not_started");

      // Overdue
      const { count: overdue } = await supabase
        .from("custom_tasks")
        .select("*", { count: "exact", head: true })
        .eq("is_admin_assigned", true)
        .neq("status", "completed")
        .lt("due_date", format(today, "yyyy-MM-dd"));

      // Completed this month
      const { count: completedThisMonth } = await supabase
        .from("custom_tasks")
        .select("*", { count: "exact", head: true })
        .eq("is_admin_assigned", true)
        .eq("status", "completed")
        .gte("completed_at", format(monthStart, "yyyy-MM-dd"));

      return {
        totalAdmin: totalAdmin || 0,
        pending: pending || 0,
        overdue: overdue || 0,
        completedThisMonth: completedThisMonth || 0,
      };
    },
  });

  // Quick complete mutation
  const quickCompleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from("custom_tasks")
        .update({ status: "completed", completed_at: new Date().toISOString() })
        .eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Task marked as completed");
      queryClient.invalidateQueries({ queryKey: ["admin-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["admin-task-stats"] });
    },
    onError: () => {
      toast.error("Failed to update task");
    },
  });

  const tasks = tasksData?.tasks || [];
  const totalTasks = tasksData?.total || 0;
  const totalPages = Math.ceil(totalTasks / pageSize);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Task Management</h1>
          <p className="text-muted-foreground">Manage and monitor tasks across all members</p>
        </div>
        {canCreateTasks && (
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Task for Member
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <ClipboardList className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Admin Tasks</p>
                <p className="text-2xl font-bold">{stats?.totalAdmin ?? "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats?.pending ?? "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold">{stats?.overdue ?? "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed This Month</p>
                <p className="text-2xl font-bold">{stats?.completedThisMonth ?? "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by task title..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Task List - Mobile Card View */}
      {isMobile ? (
        <div className="space-y-3">
          {tasksLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </CardContent>
              </Card>
            ))
          ) : tasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {searchTerm || statusFilter !== "all" || categoryFilter !== "all" || sourceFilter !== "all"
                  ? "No tasks found matching your criteria"
                  : "No admin-assigned tasks yet."}
              </CardContent>
            </Card>
          ) : (
            tasks.map((task: any) => {
              const isOverdue =
                task.due_date &&
                new Date(task.due_date) < new Date() &&
                task.status !== "completed";

              return (
                <Card key={task.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-medium text-sm line-clamp-2">{task.task_title}</h3>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {canCreateTasks && (
                            <DropdownMenuItem>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canCreateTasks && task.status !== "completed" && (
                            <DropdownMenuItem onClick={() => quickCompleteMutation.mutate(task.id)}>
                              <Check className="h-4 w-4 mr-2" />
                              Quick Complete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {getStatusBadge(task.status)}
                      {task.is_admin_assigned ? (
                        <Badge className="bg-amber-100 text-amber-700">Admin</Badge>
                      ) : (
                        <Badge variant="secondary">User</Badge>
                      )}
                      {isOverdue && <Badge variant="destructive" className="text-xs">Overdue</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Member: {task.profiles?.full_name || "Unknown"}</p>
                      {task.unas?.name && <p>UNA: {task.unas.name}</p>}
                      <p>Due: {task.due_date ? format(new Date(task.due_date), "MMM d, yyyy") : "—"}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Task Title</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>UNA</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasksLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                    {searchTerm || statusFilter !== "all" || categoryFilter !== "all" || sourceFilter !== "all"
                      ? "No tasks found matching your criteria"
                      : "No admin-assigned tasks yet. Click 'Create Task for Member' to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task: any) => {
                  const isOverdue =
                    task.due_date &&
                    new Date(task.due_date) < new Date() &&
                    task.status !== "completed";

                  return (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">
                        <span className="truncate block max-w-[280px]" title={task.task_title}>
                          {task.task_title}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="truncate block max-w-[120px]">
                          {task.profiles?.full_name || "Unknown"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="truncate block max-w-[120px]">
                          {task.unas?.name || "—"}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(task.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {task.due_date ? format(new Date(task.due_date), "MMM d, yyyy") : "—"}
                          {isOverdue && (
                            <Badge variant="destructive" className="text-xs">Overdue</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getCategoryLabel(task.category_type)}</Badge>
                      </TableCell>
                      <TableCell>
                        {task.is_admin_assigned ? (
                          <Badge className="bg-amber-100 text-amber-700">Admin</Badge>
                        ) : (
                          <Badge variant="secondary">User</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {task.created_at ? format(new Date(task.created_at), "MMM d") : "—"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {canCreateTasks && (
                              <DropdownMenuItem>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canCreateTasks && task.status !== "completed" && (
                              <DropdownMenuItem
                                onClick={() => quickCompleteMutation.mutate(task.id)}
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Quick Complete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalTasks)} of {totalTasks} tasks
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      <AdminTaskCreationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={() => {
          queryClient.invalidateQueries({ queryKey: ["admin-tasks"] });
          queryClient.invalidateQueries({ queryKey: ["admin-task-stats"] });
        }}
      />
    </div>
  );
}
